"use client";

import { IBM_Plex_Mono } from "next/font/google";
import { useGSAP } from "@gsap/react";
import React, { useRef } from "react";
import Image from "next/image";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTranslations } from "next-intl";

// // Register ScrollTrigger plugin
// if (typeof window !== "undefined") {
//   gsap.registerPlugin(ScrollTrigger);
// }

const CoreValueSection = () => {
   // Refs cho tất cả các phần tử cần animate
   const containerRef = useRef(null);
   const childrenRef = useRef<HTMLDivElement>(null); // Ref cho list con
   const videoContainerRef = useRef(null); // Ref cho khối video + text
   const coreValuesListRef = useRef(null); // Ref cho khối list bên phải
   const scrollbarThumbRef = useRef(null); // Ref cho thanh cuộn giả
   const t = useTranslations('about-us');

   const dataCoreValue = {
      title: t('explore-the-vision-mission-and-core-values-of-hyra-network'),
   };

   const dataCoreValueList = [
      {  
         type: "Mission",
         description: t('hyra-network-is-building-a-platform-that-empowers-everyone-to-join-the-ai-market-grow-together-and-access-resources-and-opportunities-fairly-driving-continuous-innovation'),
      },
      {
         type: "Vision",
         description: t('hyra-network-strives-to-be-a-leading-platform-for-ai-infrastructure-building-a-decentralized-blockchain-ecosystem-to-make-ai-more-accessible-worldwide'),
      },
      {
         type: "Core Values",
         description: t('hyra-network-builds-a-secure-inclusive-and-sustainable-ai-economy-together'),
         children: [
            {
               title: t('sustainability'),
               description: t('enabling-a-sustainable-sharing-economy-helping-people-earn-passive-income-while-contributing-responsibly-to-technological-advancement-with-a-commitment-to-economic-and-social-sustainability'),
            },
            {
               title: t('collaboration'),
               description: t('encouraging-global-collaboration-between-individuals-and-organizations-to-optimize-resources-and-knowledge-we-believe-that-the-power-of-community-is-key-to-achieving-sustainable-success'),
            },
            {
               title: t('ai-accessibility'),
               description: t('expanding-access-to-ai-technology-enabling-people-worldwide-to-use-and-benefit-from-it-hyperas-chain-provides-opportunities-for-everyone-to-access-ai-not-just-limited-to-large-enterprises'),
            },
            {
               title: t('transparency-and-security'),
               description: t('ensuring-all-transactions-and-activities-on-the-platform-are-transparent-secure-and-verifiable-this-is-our-commitment-to-maintaining-trust-and-confidence-within-the-community'),
            },
            {
               title: t('innovation-and-economic-development'),
               description: t('expanding-economic-opportunities-for-individuals-and-organizations-by-participating-in-the-ai-earning-ecosystem-aitraintoearn-fostering-skill-and-resource-development-we-drive-continuous-innovation-and-growth-bringing-economic-benefits-to-the-community'),
            },
         ],
      },
   ];
   
   useGSAP(() => {
      const childrenList = childrenRef.current;
      if (!childrenList) return;
      const isDesktop = window.innerWidth >= 1280;
      
      const initAnimations = () => {
         const internalScrollDistance = childrenList.scrollHeight - childrenList.clientHeight;
         const SCROLLBAR_TRACK_HEIGHT = 500;
         const SCROLLBAR_THUMB_HEIGHT = 160;
         const scrollbarMaxY = SCROLLBAR_TRACK_HEIGHT - SCROLLBAR_THUMB_HEIGHT;

         const masterTimeline = gsap.timeline({
            scrollTrigger: {
               trigger: containerRef.current,
               pin: true,
               scrub: 1.5,
               anticipatePin: 1,
            }
         });

         masterTimeline
            .to(".slide-0-text", { autoAlpha: 0, duration: 0.5 })
            .fromTo(".slide-1-text", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5 })
            .to({}, { duration: 1 }); 
         masterTimeline.addLabel("showCoreValues");
         if (isDesktop) {
            masterTimeline
               .to(".slide-1-text", { autoAlpha: 0, duration: 0.5 }, "showCoreValues")
               .to(videoContainerRef.current, { x: "-50%", y: "-10%", width: 630, height: 671, ease: "power2.inOut", duration: 1 }, "showCoreValues")
               .to(".video-bg", { scale: 1, width: 630, height: 671, ease: "power2.inOut", duration: 1 }, "<")
               .fromTo(coreValuesListRef.current, { autoAlpha: 0, y: 100 }, { autoAlpha: 1, y: 0, ease: "power2.out" }, "showCoreValues+=0.3")
               .fromTo(".slide-2-text", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5 }, "showCoreValues+=0.3");

            if (internalScrollDistance > 0) {
               masterTimeline.to(childrenList, {
                  scrollTop: internalScrollDistance,
                  ease: "none"
               });
               masterTimeline.to(scrollbarThumbRef.current, {
                  y: scrollbarMaxY,
                  ease: "none"
               }, "<");
            }
         } else {
            masterTimeline
               .to(".slide-1-text", { autoAlpha: 0, duration: 0.5 }, "showCoreValues")
               .fromTo(".slide-2-text", { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5 }, "showCoreValues+=0.3");
         }
      };
      
      const timeoutId = setTimeout(initAnimations, 100);

      return () => {
         clearTimeout(timeoutId);
         ScrollTrigger.getAll().forEach(st => st.kill());
      };

   }, { scope: containerRef });

   return (
      <div ref={containerRef} className="relative py-20 xl:py-16 overflow-hidden">
         <div className="sticky top-0 px-4 sm:px-[105px] xl:px-[78px] flex flex-col items-center xl:py-10 xl:h-screen">
            <h2 
               className="font-normal text-2xl relative z-20 xl:text-[48px] text-center xl:leading-[56px] text-[#202020] max-w-[800px]"
               dangerouslySetInnerHTML={{ __html: dataCoreValue.title }}
            />

            <div ref={videoContainerRef} className="relative flex justify-center items-center w-full h-[400px] xl:h-[700px] overflow-hidden xl:overflow-visible z-1">
               <div className="absolute z-1 left-0 -top-10 xl:top-5 w-full h-[450px] xl:h-full overflow-hidden xl:overflow-visible flex justify-center">
                  <video className="object-cover w-full h-full scale-[103%] [-webkit-mask-image:radial-gradient(circle_at_center,black_100%,transparent_0%)] [mask-image:radial-gradient(circle_at_center,black_100%,transparent_0%)]" autoPlay loop muted playsInline>
                     <source src="https://cdn-r2.hyra.network/hyra-network/video_core_value.webm" type="video/webm" />
                  </video>
               </div>
               <div className="relative z-20 flex items-center text-center w-[246px] xl:w-[370px] xl:pt-10">
                  {/* Slide 0: Mission */}
                  <div className="slide-0-text absolute inset-0 flex flex-col items-center justify-center space-y-4 xl:space-y-[26px]">
                     <span className={`font-open-sans text-[#0066FF] text-sm xl:text-base font-normal`}>{dataCoreValueList[0].type}</span>
                     <p className="font-normal text-base xl:text-2xl text-[#202020]">{dataCoreValueList[0].description}</p>
                  </div>
                  {/* Slide 1: Vision */}
                  <div className="slide-1-text absolute inset-0 flex flex-col items-center justify-center space-y-4 xl:space-y-[26px] opacity-0">
                     <span className={`font-open-sans text-[#0066FF] text-sm xl:text-base font-normal`}>{dataCoreValueList[1].type}</span>
                     <p className="font-normal text-base xl:text-2xl text-[#202020]">{dataCoreValueList[1].description}</p>
                  </div>
                  <div className="slide-2-text absolute inset-0 flex flex-col items-center justify-center space-y-4 xl:space-y-[26px] opacity-0 invisible">
                     <span className={`font-open-sans text-[#0066FF] text-sm xl:text-base font-normal`}>{dataCoreValueList[2].type}</span>
                     <p className="font-normal text-base xl:text-2xl text-[#202020]">{dataCoreValueList[2].description}</p>
                  </div>
               </div>
            </div>

            <div ref={coreValuesListRef} className="hidden absolute pl-4 xl:top-52 right-0 xl:flex gap-x-6 w-1/2">
               <div className="relative z-20 h-[524px] flex justify-center gap-x-1">
                  <div className="relative z-20 w-[1px] h-full bg-gradient-to-b from-blue-600/0 via-blue-600 to-blue-600/0" />
                  <div ref={scrollbarThumbRef} className="w-[5px] h-40 bg-gradient-to-b from-blue-600/0 via-blue-600 to-blue-600/0 rounded-full" />
                  <div className="w-[1px] h-full bg-gradient-to-b from-blue-600/0 via-blue-600 to-blue-600/0" />
               </div>

               <div ref={childrenRef} className="space-y-12 max-h-[600px] overflow-y-auto pr-[109px]" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                  {dataCoreValueList[2].children?.map((child, idx) => (
                     <div key={idx}>
                        <div className="flex gap-x-6">
                           <Image src={"/images/icons/dot-2.svg"} className="h-fit" width={24} height={29} alt="icon_dot" />
                           <div className="space-y-[17px]">
                              <h3 className="text-2xl font-normal text-[#000000]">{child.title}</h3>
                              <p className="text-base font-light text-[#646464]">{child.description}</p>
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </div>
      </div>
   );
};

export default CoreValueSection;